/**
 * Action creator helpers for the custom-shape editor.
 * Use with dispatch: dispatch(editorActions.removeNode(nodeId))
 */

/**
 * @param {string} nodeId
 * @returns {{ type: 'REMOVE_NODE', payload: { nodeId } }}
 */
export function removeNode(nodeId) {
    return {type: 'REMOVE_NODE', payload: {nodeId}}
}

/**
 * @param {string[]} nodeIds
 * @returns {{ type: 'REMOVE_NODES', payload: { nodeIds } }}
 */
export function removeNodes(nodeIds) {
    return {type: 'REMOVE_NODES', payload: {nodeIds}}
}

/**
 * @param {string} nodeId
 * @param {object} updates
 * @param {{ recordHistory?: boolean }} [meta]
 * @returns {{ type: 'UPDATE_NODE', payload: { nodeId, updates }, meta?: object }}
 */
export function updateNode(nodeId, updates, meta = {recordHistory: true}) {
    return {
        type: 'UPDATE_NODE',
        payload: {nodeId, updates},
        meta,
    }
}

/**
 * Batch update multiple nodes in a single dispatch.
 * @param {Array<{ nodeId: string, updates: object }>} entries
 * @param {{ recordHistory?: boolean }} [meta]
 * @returns {{ type: 'UPDATE_NODES', payload: { entries }, meta?: object }}
 */
export function updateNodes(entries, meta = {recordHistory: true}) {
    return {
        type: 'UPDATE_NODES',
        payload: {entries},
        meta,
    }
}

/**
 * Semantic text update command. Only applies to nodes with type === 'text'.
 * @param {string} nodeId
 * @param {string} text
 * @param {{ recordHistory?: boolean }} [meta]
 * @returns {{ type: 'UPDATE_TEXT', payload: { nodeId, text }, meta?: object }}
 */
export function updateText(nodeId, text, meta = {recordHistory: true}) {
    return {
        type: 'UPDATE_TEXT',
        payload: {nodeId, text},
        meta,
    }
}

/**
 * @param {string} nodeId
 * @param {boolean} [recordHistory=false]
 * @returns {{ type: 'SELECT_NODE', payload: { nodeId }, meta: { recordHistory: boolean } }}
 */
export function selectNode(nodeId, recordHistory = false) {
    return {
        type: 'SELECT_NODE',
        payload: {nodeId},
        meta: {recordHistory},
    }
}

/**
 * @param {string} nodeId
 * @returns {{ type: 'ADD_TO_SELECTION', payload: { nodeId }, meta: { recordHistory: false } }}
 */
export function addToSelection(nodeId) {
    return {
        type: 'ADD_TO_SELECTION',
        payload: {nodeId},
        meta: {recordHistory: false},
    }
}

/**
 * @param {string} nodeId
 * @param {number} toIndex
 * @param {string} [source='ui']
 * @returns {{ type: 'REORDER', payload: { nodeId, toIndex }, meta: { source: string } }}
 */
export function reorder(nodeId, toIndex, source = 'ui') {
    return {
        type: 'REORDER',
        payload: {nodeId, toIndex},
        meta: {source},
    }
}

/**
 * @param {object} node
 * @param {number} [index=0]
 * @param {boolean} [select=true]
 * @returns {{ type: 'ADD_NODE', payload: { node, index, select } }}
 */
export function addNode(node, index = 0, select = true) {
    return {
        type: 'ADD_NODE',
        payload: {node, index, select},
    }
}

/**
 * @param {string[]} nodeIds
 * @returns {{ type: 'DESELECT_NODES', payload: { nodeIds }, meta: { recordHistory: false } }}
 */
export function deselectNodes(nodeIds) {
    return {
        type: 'DESELECT_NODES',
        payload: {nodeIds},
        meta: {recordHistory: false},
    }
}

/**
 * @param {number} dx
 * @param {number} dy
 * @returns {{ type: 'PAN_VIEWPORT', payload: { dx, dy }, meta: { recordHistory: false } }}
 */
export function panViewport(dx, dy) {
    return {
        type: 'PAN_VIEWPORT',
        payload: {dx, dy},
        meta: {recordHistory: false},
    }
}

/**
 * @param {object} payload - { scale, center, pointer }
 * @returns {{ type: 'SET_ZOOM', payload: object, meta: { recordHistory: false } }}
 */
export function setZoom(payload) {
    return {
        type: 'SET_ZOOM',
        payload,
        meta: {recordHistory: false},
    }
}
